/* eslint-disable no-return-assign */
import { AxiosError } from "axios";
import { FirebaseError } from "firebase/app";

import { dialogMessage } from "../../constants/dialogMessage";
import { Locale } from "../types";

export interface ErrorMessage {
  title: string;
  message: string;
  buttons: {
    text: string;
    onClick?: () => void;
    colorScheme?: string;
    w?: string;
    isCancelButton?: boolean;
  }[];
}

// FIXME: 다국어지원
const INQUIRY_URLS = {
  ko: "https://moais.atlassian.net/servicedesk/customer/portal/1/group/4/create/17",
  en: "https://moais.atlassian.net/servicedesk/customer/portal/1/group/5/create/23",
  ja: "https://moais.atlassian.net/servicedesk/customer/portal/1/group/5/create/23",
  fr: "https://moais.atlassian.net/servicedesk/customer/portal/1/group/5/create/23",
  es: "https://moais.atlassian.net/servicedesk/customer/portal/1/group/5/create/23",
  "zh-CN":
    "https://moais.atlassian.net/servicedesk/customer/portal/1/group/5/create/23",
  "zh-TW":
    "https://moais.atlassian.net/servicedesk/customer/portal/1/group/5/create/23",
  de: "https://moais.atlassian.net/servicedesk/customer/portal/1/group/5/create/23",
  it: "https://moais.atlassian.net/servicedesk/customer/portal/1/group/5/create/23",
  sv: "https://moais.atlassian.net/servicedesk/customer/portal/1/group/5/create/23",
};

export const BUTTONS = (locale: Locale) => ({
  LOGIN_ERROR_CONFIRM: [
    {
      text: dialogMessage.confirm,
      colorScheme: "blue",
      w: "full",
      isCancelButton: true,
    },
  ],
  LOGOUT_ERROR_CONFIRM: [
    {
      text: dialogMessage.confirm,
      colorScheme: "blue",
      w: "full",
      isCancelButton: true,
    },
  ],
  CONFIRM_PASSWORD_ERROR_CONFIRM: [
    {
      text: dialogMessage.confirm,
      colorScheme: "blue",
      w: "full",
      isCancelButton: true,
    },
  ],
  DELETE_ACCOUNT_REQUEST: [
    {
      text: dialogMessage.cancel,
      colorScheme: "blue",
      w: "50%",
      isCancelButton: true,
    },
    {
      text: dialogMessage.withdrawalRequest.buttons[0].confirm,
      colorScheme: "red",
      w: "50%",
    },
  ],
  DELETE_ACCOUNT_ERROR_INQUIRY: [
    {
      text: dialogMessage.withdrawalFailed[500].buttons[0].text,
      // eslint-disable-next-line security/detect-object-injection -- locale은 Locale union 타입, 리터럴 Record 읽기 전용 접근
      onClick: () => window.open(INQUIRY_URLS[locale], "_blank"),
      colorScheme: "blue",
      w: "50%",
    },
    {
      text: dialogMessage.confirm,
      colorScheme: "blue",
      w: "50%",
      isCancelButton: true,
    },
  ],
  DELETE_ACCOUNT_SUCCESS: [
    {
      text: dialogMessage.withdrawalSuccess.buttons[0].text,
      onClick: () =>
        // 회원탈퇴 성공 시 메인 페이지로 이동. 탈퇴한 계정의 클라이언트 상태가
        // 남지 않도록 router.push가 아닌 전체 문서 로드로 리셋한다.
        // eslint-disable-next-line @next/next/no-location-assign-relative-destination
        (window.location.href = "/"),
      colorScheme: "blue",
      w: "100%",
    },
  ],
});

// ! : 로그인 실패 시 에러 메시지
export const getFirebaseAuthErrorMessage = (
  err: unknown,
  locale: Locale,
  reAuth?: boolean
): ErrorMessage => {
  if (err instanceof FirebaseError) {
    switch (err.code) {
      // !: 전화번호 로그인 - 등록이 안된 계정이거나 비밀번호가 틀린 경우
      // !: Firebase Auth에 미등록 됐거나 등록한 아이디 또는 비밀번호가 다를 경우
      // !: 이미 탈퇴한 사용자 일 때
      case "auth/user-not-found":
        return {
          title:
            dialogMessage.loginFailed
              .phoneNumberLoginUnregisteredAccountOrWrongPassword.title,
          message:
            dialogMessage.loginFailed
              .phoneNumberLoginUnregisteredAccountOrWrongPassword.message,
          buttons: BUTTONS(locale).LOGIN_ERROR_CONFIRM,
        };
      // !: 전화번호 로그인 - 등록이 안된 계정이거나 비밀번호가 틀린 경우
      // !: Firebase Auth에 미등록 됐거나 등록한 아이디 또는 비밀번호가 다를 경우
      case "auth/wrong-password":
        return {
          title: reAuth
            ? // 비밀번호 재확인인 경우는 비밀번호가 다른 경우에 대해 구체적인 메시지를 보여줌
              dialogMessage.reAuthenticationPassword.title
            : dialogMessage.loginFailed
                .firebaseAuthUnregisteredOrWrongCredentials.title,

          message: reAuth
            ? // 비밀번호 재확인인 경우는 비밀번호가 다른 경우에 대해 구체적인 메시지를 보여줌
              dialogMessage.reAuthenticationPassword.message
            : dialogMessage.loginFailed
                .firebaseAuthUnregisteredOrWrongCredentials.message,

          buttons: BUTTONS(locale).LOGIN_ERROR_CONFIRM,
        };
      // !: 일정 횟수 이상 시도하여 로그인이 불가는 할 때
      case "auth/too-many-requests":
        return {
          title: dialogMessage.loginFailed.loginAttemptsExceededTryLater.title,
          message:
            dialogMessage.loginFailed.loginAttemptsExceededTryLater.message,
          buttons: BUTTONS(locale).LOGIN_ERROR_CONFIRM,
        };
      default:
    }
  }
  // !: 소셜 로그인 시 해당 계정에 에러가 발생한 경우
  // !: 이 외 알 수 없는 에러로 실패할 경우
  return {
    title: dialogMessage.loginFailed.socialAccountLoginErrorPleaseRetry.title,

    message:
      dialogMessage.loginFailed.socialAccountLoginErrorPleaseRetry.message,

    buttons: BUTTONS(locale).LOGIN_ERROR_CONFIRM,
  };
};

// ! : 회원탈퇴 실패 시 에러 메시지, 현재는 404, 500만 발생할 것으로 예상
export const getDeleteAccountErrorMessage = (
  err: unknown,
  locale: Locale
): ErrorMessage => {
  if (err instanceof AxiosError) {
    const errorStatus = err.response?.status || 200;

    switch (errorStatus) {
      case 404:
        return {
          title: dialogMessage.withdrawalFailed[404].title,
          message: dialogMessage.withdrawalFailed[404].message,
          buttons: [BUTTONS(locale).DELETE_ACCOUNT_ERROR_INQUIRY[1]],
        };
      case 500:
        return {
          title: dialogMessage.withdrawalFailed[500].title,
          message: dialogMessage.withdrawalFailed[500].message,
          buttons: BUTTONS(locale).DELETE_ACCOUNT_ERROR_INQUIRY,
        };
      default:
    }
  }
  return {
    title: dialogMessage.withdrawalFailed[404].title,
    message: dialogMessage.withdrawalFailed[404].message,
    buttons: [BUTTONS(locale).DELETE_ACCOUNT_ERROR_INQUIRY[1]],
  };
};
